"use client"

import { useMemo } from "react"
import { Activity, Banknote, Target } from "lucide-react"

import { EVIDENCE_LADDER, resolvePolicy } from "@/modules/lernpfad/engine"
import type { EvidenceInstrument } from "@/modules/lernpfad/engine"
import type { PopulationSummary } from "@/modules/lernpfad/engine/aggregate"
import { computeLevel4 } from "@/modules/lernpfad/data/outcomes"
import type { OutcomeIndicator } from "@/modules/lernpfad/data/outcomes"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import type { UiKey } from "@/modules/lernpfad/i18n/ui"
import { SEQUENTIAL_VAR, STATUS_VAR } from "../charts/palette"
import { SectionHeading } from "../ui-bits"

/**
 * Wirkungsmessung for the whole organisation.
 *
 * Three blocks, in the order the argument has to be made: what the transcript
 * actually proves, how far the evidence reaches up the Kirkpatrick chain, and
 * what came out the other end.
 *
 * The middle block is deliberately NOT drawn as a funnel. Levels 1 and 2 are
 * independent measurements, not sequential gates — mandatory training records a
 * test result for nearly everyone and a feedback survey for far fewer, so Level
 * 2 legitimately runs ahead of Level 1. A funnel would imply a dependency that
 * does not exist and would make the real data look like a bug.
 */

export default function WirkungPanel({
    state,
    summary,
}: {
    state: LernpfadDemoState
    summary: PopulationSummary
}) {
    const { t, localizer, policy } = state
    const resolved = resolvePolicy(policy)
    const level4 = useMemo(() => computeLevel4(summary), [summary])

    const totalCompletions = summary.kirkpatrick.completions || 1
    const pct = (value: number) => (value / totalCompletions) * 100

    const chain: Array<{ key: UiKey; value: number; level: string }> = [
        { key: "wirkung.l1", value: summary.kirkpatrick.reaction, level: "1" },
        { key: "wirkung.l1target", value: summary.kirkpatrick.reactionOnTarget, level: "1" },
        { key: "wirkung.l2", value: summary.kirkpatrick.learning, level: "2" },
        { key: "wirkung.bridge", value: summary.kirkpatrick.bridge, level: "2→3" },
        { key: "wirkung.l3", value: summary.kirkpatrick.behaviour, level: "3" },
        { key: "wirkung.l3verified", value: summary.kirkpatrick.behaviourVerified, level: "3" },
    ]

    return (
        <div className="space-y-5">
            <SectionHeading icon={Activity} title={t("wirkung.title")} />

            {/* What the transcript proves */}
            <div className="lp-card rounded-xl border bg-card p-4">
                <h4 className="text-sm font-medium">{t("wirkung.evidenceMix")}</h4>
                <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
                    {t("wirkung.evidenceMixHint", {
                        count: localizer.num(summary.kirkpatrick.completions),
                    })}
                </p>
                <div className="flex h-8 w-full overflow-hidden rounded-md">
                    {EVIDENCE_LADDER.map((instrument, index) => {
                        const share = pct(summary.evidenceMix[instrument])
                        if (share <= 0) return null
                        return (
                            <div
                                key={instrument}
                                className="flex items-center justify-center text-xs font-medium text-white"
                                style={{
                                    width: `${share}%`,
                                    // A single-hue ramp: this is one quantity at
                                    // increasing strength, not four categories.
                                    background: SEQUENTIAL_VAR,
                                    opacity: 0.35 + index * 0.22,
                                }}
                                title={`${t(`instrument.${instrument}`)}: ${Math.round(share)} %`}
                            >
                                {share >= 8 ? `${Math.round(share)} %` : ""}
                            </div>
                        )
                    })}
                </div>
                <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-4">
                    {EVIDENCE_LADDER.map((instrument: EvidenceInstrument, index) => (
                        <li key={instrument} className="flex items-center gap-1.5 text-xs">
                            <span
                                aria-hidden
                                className="size-2 shrink-0 rounded-sm"
                                style={{ background: SEQUENTIAL_VAR, opacity: 0.35 + index * 0.22 }}
                            />
                            <span className="truncate text-muted-foreground">
                                {t(`instrument.${instrument}`)}
                            </span>
                            <span className="ml-auto font-medium tabular-nums">
                                {localizer.num(summary.evidenceMix[instrument])}
                            </span>
                        </li>
                    ))}
                </ul>
                <p className="mt-3 border-t pt-2 text-xs leading-relaxed text-muted-foreground">
                    {t("wirkung.floorNote", {
                        floor: t(`instrument.${resolved.evidence.floor}`),
                        share: Math.round(
                            EVIDENCE_LADDER.slice(EVIDENCE_LADDER.indexOf(resolved.evidence.floor))
                                .reduce((sum, key) => sum + summary.evidenceMix[key], 0) /
                                totalCompletions *
                                100,
                        ),
                    })}
                </p>
            </div>

            {/* How far the evidence reaches */}
            <div className="lp-card rounded-xl border bg-card p-4">
                <h4 className="text-sm font-medium">{t("wirkung.chain")}</h4>
                <p className="mb-3 mt-0.5 text-xs text-muted-foreground">{t("wirkung.chainHint")}</p>
                <ul className="space-y-2">
                    {chain.map((row) => (
                        <li key={row.key} className="space-y-1">
                            <div className="flex items-baseline justify-between gap-3 text-xs">
                                <span>
                                    <span className="mr-1.5 rounded bg-muted px-1 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                                        {row.level}
                                    </span>
                                    {t(row.key)}
                                </span>
                                <span className="shrink-0 tabular-nums">
                                    {localizer.num(row.value)}
                                    <span className="ml-1.5 text-muted-foreground">
                                        {Math.round(pct(row.value))} %
                                    </span>
                                </span>
                            </div>
                            <div
                                className="h-1.5 w-full overflow-hidden rounded-full"
                                style={{ background: STATUS_VAR.track }}
                            >
                                <div
                                    className="h-full rounded-full"
                                    style={{
                                        width: `${Math.min(100, pct(row.value))}%`,
                                        background: SEQUENTIAL_VAR,
                                    }}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Level 4 */}
            <div className="lp-card rounded-xl border bg-card p-4">
                <h4 className="flex items-center gap-1.5 text-sm font-medium">
                    <Target className="size-3.5 text-muted-foreground" />
                    {t("wirkung.l4")}
                </h4>
                <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
                    {t("wirkung.l4Hint", {
                        from: resolved.evidence.resultsWindowDays.from,
                        to: resolved.evidence.resultsWindowDays.to,
                    })}
                </p>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[420px] text-xs">
                        <thead>
                            <tr className="border-b text-left text-muted-foreground">
                                <th className="py-1.5 font-medium">{t("wirkung.indicator")}</th>
                                <th className="py-1.5 text-right font-medium">{t("wirkung.before")}</th>
                                <th className="py-1.5 text-right font-medium">{t("wirkung.after")}</th>
                                <th className="py-1.5 text-right font-medium">{t("wirkung.change")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {level4.indicators.map((indicator) => (
                                <IndicatorRow
                                    key={indicator.id}
                                    indicator={indicator}
                                    label={t(`wirkung.kpi.${indicator.id}` as UiKey)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 grid gap-3 border-t pt-3 sm:grid-cols-3">
                    <Figure
                        label={t("wirkung.benefit")}
                        value={`USD ${localizer.num(level4.netBenefitUsd)}`}
                    />
                    <Figure
                        label={t("wirkung.cost")}
                        value={`USD ${localizer.num(level4.costUsd)}`}
                    />
                    <Figure
                        icon={Banknote}
                        label={t("wirkung.roi")}
                        value={`${level4.roi > 0 ? "+" : ""}${level4.roi.toFixed(1)} %`}
                        tone={level4.roi >= 0 ? "good" : "warn"}
                    />
                </div>

                <p className="mt-3 rounded-md bg-muted/40 p-2.5 text-xs leading-relaxed text-muted-foreground">
                    {t("wirkung.method", {
                        isolation: Math.round(level4.isolationFactor * 100),
                        confidence: Math.round(level4.confidence * 100),
                        gross: localizer.num(level4.grossBenefitUsd),
                        bcr: level4.bcr.toFixed(2),
                        transfer: Math.round(level4.transferRate * 100),
                    })}
                </p>
            </div>
        </div>
    )
}

function IndicatorRow({ indicator, label }: { indicator: OutcomeIndicator; label: string }) {
    const delta = indicator.current - indicator.baseline
    const improved = indicator.direction === "down" ? delta < 0 : delta > 0
    const unit = indicator.unit === "percent" ? " %" : indicator.unit === "days" ? " d" : ""
    return (
        <tr className="border-b last:border-0">
            <td className="py-1.5">{label}</td>
            <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                {indicator.baseline}
                {unit}
            </td>
            <td className="py-1.5 text-right font-medium tabular-nums">
                {indicator.current}
                {unit}
            </td>
            <td
                className="py-1.5 text-right tabular-nums"
                style={{ color: improved ? "var(--lp-good)" : "var(--lp-warning)" }}
            >
                {delta > 0 ? "+" : ""}
                {Math.round(delta * 10) / 10}
                {unit}
            </td>
        </tr>
    )
}

function Figure({
    icon: Icon,
    label,
    value,
    tone,
}: {
    icon?: typeof Banknote
    label: string
    value: string
    tone?: "good" | "warn"
}) {
    return (
        <div className="rounded-lg border bg-muted/25 p-2.5">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                {Icon && <Icon className="size-3" />}
                {label}
            </p>
            <p
                className="mt-0.5 text-base font-semibold tabular-nums"
                style={
                    tone ? { color: tone === "good" ? "var(--lp-good)" : "var(--lp-warning)" } : undefined
                }
            >
                {value}
            </p>
        </div>
    )
}
